import { useEffect, useRef, useState } from 'react';
import type { ObjectiveSummaryView } from '@contracts';
import { Plus, X } from '@ui/shared/icons';
import { PickerOptionRow } from './FormPrimitives';

/**
 * "Linked objectives" (design/screens/objective-create-desktop.md SS7.7).
 * Purely informational/navigational (R3.13/AC-O19) — no rollup math renders
 * here, matching `linkedHabits`'s own display-only pattern. Reuses the same
 * option-row popover interaction shape as `ObjectivePickerPopover`
 * (habit-form.md SS1.5), scaled to allow linking more than one objective per
 * open (each selection adds a row and keeps the popover open).
 */
export function LinkedObjectivesSection({
  linked,
  candidates,
  onLink,
  onUnlink,
  onOpenObjective,
}: {
  readonly linked: ReadonlyArray<{ readonly objectiveId: string; readonly title: string }>;
  /** Active/draft objectives eligible to link, excluding self and already-linked. */
  readonly candidates: readonly ObjectiveSummaryView[];
  readonly onLink: (objectiveId: string) => void;
  readonly onUnlink: (objectiveId: string) => void;
  readonly onOpenObjective?: (objectiveId: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ marginTop: 'var(--space-5)', paddingTop: 'var(--space-5)', borderTop: '1px solid var(--border-subtle)' }}>
      <span
        style={{
          fontSize: 'var(--type-label-size)',
          fontWeight: 'var(--type-label-weight)',
          letterSpacing: 'var(--type-label-tracking)',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        Linked objectives
      </span>

      {linked.length === 0 ? (
        <p style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          No linked objectives yet. Link one if it&apos;s part of the same bigger push.
        </p>
      ) : (
        <ul style={{ marginTop: 'var(--space-2)', listStyle: 'none', padding: 0, margin: 0 }}>
          {linked.map((entry) => (
            <li key={entry.objectiveId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)', padding: 'var(--space-1) 0' }}>
              <button
                type="button"
                onClick={() => onOpenObjective?.(entry.objectiveId)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: 'left',
                  fontSize: 'var(--type-body-size)',
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {entry.title}
              </button>
              <button
                type="button"
                aria-label={`Unlink ${entry.title}`}
                onClick={() => onUnlink(entry.objectiveId)}
                style={{ width: 'var(--touch-target-min)', height: 'var(--touch-target-min)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', flex: 'none' }}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div style={{ position: 'relative', marginTop: 'var(--space-2)' }}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            minHeight: 'var(--touch-target-min)',
            color: 'var(--hue-primary)',
            fontWeight: 600,
            fontSize: 'var(--type-caption-size)',
          }}
        >
          <Plus size={14} aria-hidden="true" />
          Link an objective
        </button>
        {open && (
          <LinkObjectivePopover
            candidates={candidates}
            onSelect={(objectiveId) => onLink(objectiveId)}
            onClose={() => setOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

function LinkObjectivePopover({
  candidates,
  onSelect,
  onClose,
}: {
  readonly candidates: readonly ObjectiveSummaryView[];
  readonly onSelect: (objectiveId: string) => void;
  readonly onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handlePointerDown(e: MouseEvent) {
      if (ref.current?.contains(e.target as Node)) return;
      onClose();
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    const id = window.setTimeout(() => {
      document.addEventListener('mousedown', handlePointerDown);
      document.addEventListener('keydown', handleKeyDown);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="listbox"
      aria-label="Link an objective"
      style={{
        position: 'absolute',
        zIndex: 41,
        top: 'calc(100% + var(--space-2))',
        left: 0,
        right: 0,
        background: 'var(--surface-overlay)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-2)',
        border: '1px solid var(--border-subtle)',
        padding: 'var(--space-2)',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '240px',
        overflowY: 'auto',
      }}
    >
      {candidates.length === 0 ? (
        <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', padding: '0 var(--space-2)' }}>
          No other objectives available to link.
        </p>
      ) : (
        candidates.map((candidate) => (
          <PickerOptionRow
            key={candidate.objectiveId}
            label={candidate.title}
            selected={false}
            onSelect={() => onSelect(candidate.objectiveId)}
          />
        ))
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-2)' }}>
        <button
          type="button"
          onClick={onClose}
          style={{ minHeight: 'var(--touch-target-min)', padding: '0 var(--space-2)', fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--hue-primary)' }}
        >
          Done
        </button>
      </div>
    </div>
  );
}
